import React from "react";
import { Box, Button, Typography, Alert, AlertTitle } from "@mui/material";

/**
 * React error boundary.
 *
 * Dashboard had NONE. Any render-time throw unmounted the entire tree and left
 * a white screen, with a reload the only recovery.
 *
 * Three tiers, by `variant`:
 *   root  — outside the router. Last resort.
 *   route — one per route element, keyed on pathname so navigating resets it.
 *           A broken Fixture page leaves nav and every other route usable.
 *   panel — around a DDS-driven widget. THE tier that matters: a malformed
 *           patch that breaks the stats table must not take down the match
 *           clock beside it.
 *
 * NOTE: a boundary cannot catch a throw during module evaluation — the module
 * graph fails before React renders, so the boundary never mounts. Check for
 * top-level throws in config modules before relying on this.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Keep console.error: index.js silences log/debug in production but leaves
    // error alone, so this is still visible in a real incident.
    console.error(`[ErrorBoundary:${this.props.variant || "root"}]`, error, info?.componentStack);
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prev) {
    // Reset when the caller says the context changed (route change, new
    // fixture, fresh data identity), so a transient failure is not sticky.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, info: null });
    }
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const { variant = "root", label } = this.props;
    const retry = () => this.setState({ error: null, info: null });

    if (variant === "panel") {
      return (
        <Alert
          severity="error"
          sx={{ m: 1 }}
          action={
            <Button color="inherit" size="small" onClick={retry}>
              Retry
            </Button>
          }
        >
          <AlertTitle>{label || "This panel failed to render"}</AlertTitle>
          <Typography variant="caption">{error.message}</Typography>
        </Alert>
      );
    }

    return (
      <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
        <Typography variant="h5">Something went wrong</Typography>
        <Typography variant="body2" color="text.secondary">
          {label ? `${label} could not be displayed.` : "This screen could not be displayed."}{" "}
          Other parts of the app should still work.
        </Typography>
        <Alert severity="error" sx={{ width: "100%", maxWidth: 900 }}>
          <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-word" }}>
            {error.message}
          </Typography>
        </Alert>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" onClick={retry}>
            Try again
          </Button>
          <Button variant="outlined" onClick={() => window.location.reload()}>
            Reload
          </Button>
          <Button
            variant="text"
            onClick={() =>
              navigator.clipboard?.writeText(
                `${error.stack || error.message}\n\nComponent stack:${info?.componentStack || ""}`
              )
            }
          >
            Copy diagnostics
          </Button>
        </Box>
      </Box>
    );
  }
}

export default ErrorBoundary;
